import { create } from 'zustand';

export type GamePhase = 
  | 'landing'
  | 'settings'
  | 'lobby'
  | 'role_selection'
  | 'character_selection'
  | 'gameplay'
  | 'day_end'
  | 'game_end';

export type PlayerRole = 'customer' | 'chef' | null;

export interface Player {
  playerId: string;
  playerName: string;
  isHost: boolean;
  isReady: boolean;
  role?: PlayerRole;
  characterId?: string;
}

export interface GameSettings {
  dayDuration: number;
  difficulty: 'easy' | 'normal' | 'hard';
  maxPlayers: number;
}

export interface Order {
  id: string;
  customerId: string;
  customerName: string;
  menuItemId: string;
  chefId?: string;
  status: 'pending' | 'cooking' | 'completed' | 'delivered';
  createdAt: number;
  cookingQuality?: number; // 0-100 score from chef's timing performance
}

export interface AvailableGame {
  gameId: string;
  hostName: string;
  playerCount: number;
  maxPlayers: number;
  difficulty: string;
  createdAt: number;
}

export interface GameState {
  // Connection state
  isConnected: boolean;
  connectionId: string | null;
  
  // Player state
  playerId: string | null;
  playerName: string;
  isHost: boolean;
  role: PlayerRole;
  characterId: string | null;
  
  // Game state
  gameId: string | null;
  joinCode: string | null;
  phase: GamePhase;
  players: Player[];
  settings: GameSettings;
  
  // Game progress
  currentDay: number;
  currentMealPeriod: string | null;
  
  // Orders
  orders: Order[];
  
  // Available games to join
  availableGames: AvailableGame[];
  
  // Error handling
  error: string | null;
  
  // Actions
  setConnected: (connected: boolean) => void;
  setConnectionId: (id: string | null) => void;
  setPlayerName: (name: string) => void;
  setPhase: (phase: GamePhase) => void;
  setError: (error: string | null) => void;
  updateSettings: (settings: Partial<GameSettings>) => void;
  handleMessage: (message: any) => void;
  addNPCOrder: (order: Order) => void;
  reset: () => void;
}

const initialState = {
  isConnected: false,
  connectionId: null,
  playerId: null,
  playerName: '',
  isHost: false,
  role: null as PlayerRole,
  characterId: null,
  gameId: null,
  joinCode: null,
  phase: 'landing' as GamePhase,
  players: [] as Player[],
  settings: {
    dayDuration: 30,
    difficulty: 'normal' as const,
    maxPlayers: 8,
  },
  currentDay: 0,
  currentMealPeriod: null,
  orders: [] as Order[],
  availableGames: [] as AvailableGame[],
  error: null,
};

export const useGameStore = create<GameState>((set, get) => ({
  ...initialState,
  
  setConnected: (isConnected) => set({ isConnected }),
  
  setConnectionId: (connectionId) => set({ connectionId }),
  
  setPlayerName: (playerName) => set({ playerName }),
  
  setPhase: (phase) => set({ phase }),
  
  setError: (error) => set({ error }),
  
  updateSettings: (newSettings) => set((state) => ({
    settings: { ...state.settings, ...newSettings },
  })),
  
  handleMessage: (message) => {
    const { type } = message;
    
    switch (type) {
      case 'GAME_CREATED':
        set({
          gameId: message.gameId,
          joinCode: message.joinCode,
          playerId: message.playerId,
          isHost: true,
          phase: 'settings',
          players: [{
            playerId: message.playerId,
            playerName: get().playerName,
            isHost: true,
            isReady: false,
          }],
          error: null,
        });
        break;
        
      case 'GAME_JOINED':
        set({
          gameId: message.gameId,
          joinCode: message.joinCode,
          playerId: message.playerId,
          isHost: false,
          phase: 'lobby',
          players: message.players,
          settings: message.settings,
          error: null,
        });
        break;
        
      case 'GAMES_LIST':
        set({ availableGames: message.games || [] });
        break;
        
      case 'PLAYER_JOINED':
        set((state) => ({
          players: [...state.players, message.player],
        }));
        break;
        
      case 'PLAYER_LEFT':
        set((state) => ({
          players: state.players.filter(p => p.playerId !== message.playerId),
        }));
        break;
        
      case 'GAME_CLOSED':
        // Host left - reset to landing with error message
        set({
          ...initialState,
          error: message.reason || 'Game has been closed',
          playerName: get().playerName, // Keep player name
        });
        break;
        
      case 'SETTINGS_UPDATED':
        set({ settings: message.settings });
        break;
        
      case 'GAME_STARTED':
        set({
          phase: message.phase || 'role_selection',
          currentDay: 1,
        });
        break;
        
      case 'ROLE_SELECTED':
        set((state) => ({
          role: message.playerId === state.playerId ? message.role : state.role,
          players: state.players.map(p => 
            p.playerId === message.playerId ? { ...p, role: message.role } : p
          ),
        }));
        break;
        
      case 'CHARACTER_SELECTED':
        set((state) => ({
          characterId: message.playerId === state.playerId ? message.characterId : state.characterId,
          players: state.players.map(p => 
            p.playerId === message.playerId ? { ...p, characterId: message.characterId } : p
          ),
        }));
        break;
        
      case 'PHASE_CHANGED':
        set({ 
          phase: message.phase,
          currentMealPeriod: message.mealPeriod || get().currentMealPeriod,
          currentDay: message.day || get().currentDay,
        });
        break;
        
      case 'ORDER_PLACED':
        set((state) => ({
          orders: [...state.orders, message.order],
        }));
        break;
        
      case 'ORDER_CLAIMED':
        set((state) => ({
          orders: state.orders.map(o => 
            o.id === message.orderId 
              ? { ...o, chefId: message.chefId, status: 'cooking' as const }
              : o
          ),
        }));
        break;
        
      case 'ORDER_COMPLETED':
        set((state) => ({
          orders: state.orders.map(o => 
            o.id === message.orderId 
              ? { ...o, status: 'completed' as const }
              : o
          ),
        }));
        break;
        
      case 'ORDER_DELIVERED':
        set((state) => ({
          orders: state.orders.map(o => 
            o.id === message.orderId 
              ? { ...o, status: 'delivered' as const }
              : o
          ),
        }));
        break;
        
      case 'MEAL_PERIOD_STARTED':
        set({ 
          currentMealPeriod: message.mealPeriod,
          phase: 'gameplay',
          orders: [], // Clear orders for new period
        });
        break;
        
      case 'DAY_ENDED':
        set({
          phase: 'day_end',
          currentDay: message.day,
        });
        break;
        
      case 'ERROR':
        set({ error: message.message });
        break;
        
      default:
        console.log('Unhandled message type:', type);
    }
  },
  
  // Add NPC order locally (for when no human customers exist)
  addNPCOrder: (order) => set((state) => ({
    orders: [...state.orders, order],
  })),
  
  reset: () => set(initialState),
}));
